/**
 * MEF EduSuite subscription prices. Plans are set in US dollars (the platform's costs — Google Cloud,
 * Gemini, Claude — are billed in dollars) and billed to Nigerian schools in naira at the term's CBN rate:
 *
 *   naira = dollars × rate, the fee per student rounded up to ₦50, the base up to ₦5,000.
 *   The rate in use changes only when the official rate has moved more than 5% since it was last set.
 *
 * A school pays a base subscription per term plus a fee per student per term. Training institutions pay
 * monthly per learner. This file is shared word for word by the server (functions/src/pricingCore.ts) and
 * the app (src/services/pricingCore.ts); change both together.
 */

export type PlanId = 'ess' | 'std' | 'pre' | 'trn' | 'ent';
/** what the platform pays dollars for, each capped per term (per month for training) */
export type Kind = 'live' | 'voice' | 'sms' | 'ai' | 'claude' | 'rec' | 'bcast' | 'video';
export type Caps = Record<Kind, number>;
export const KINDS: Kind[] = ['live', 'voice', 'sms', 'ai', 'claude', 'rec', 'bcast', 'video'];
export const KIND_LABEL: Record<Kind, { name: string; unit: (n: number) => string }> = {
    live: { name: 'Live-class hours', unit: n => `${Math.round(n / 60).toLocaleString()} h` },
    voice: { name: 'Voice tutor', unit: n => `${Math.round(n).toLocaleString()} min` },
    sms: { name: 'SMS', unit: n => Math.round(n).toLocaleString() },
    ai: { name: 'AI requests (tutor, MEF-AID, marking, lesson tools)', unit: n => Math.round(n).toLocaleString() },
    claude: { name: 'Presentations and long documents', unit: n => Math.round(n).toLocaleString() },
    rec: { name: 'Recorded class hours', unit: n => `${Math.round(n / 60).toLocaleString()} h` },
    bcast: { name: 'Broadcast hours (YouTube, Facebook, TikTok)', unit: n => `${Math.round(n / 60).toLocaleString()} h` },
    video: { name: 'AI intro videos', unit: n => Math.round(n).toLocaleString() },
};
export type Period = 'current' | 'next';

export interface PlanDef {
    id: PlanId;
    name: string;
    /** dollars per term (per month for 'trn') */
    base: number;
    /** dollars per student per term (per learner per month for 'trn') */
    per: number;
    monthly: boolean;
    /** priced by agreement: the school contacts MEF, and Super Admin sets its prices and caps */
    contact?: boolean;
    /** included each month, per student: live-class hours */
    liveHours: number;
    /** included each month, school-wide: voice-tutor minutes */
    voiceMinutes: number;
    /** included each term (each month for 'trn'), per student: SMS */
    sms: number;
    /** included each month, per student: AI requests (Gemini) */
    ai: number;
    /** included each term (month for 'trn'), school-wide: Claude calls, recorded hours, broadcast hours, AI videos */
    claude: number; recHours: number; bcastHours: number; videos: number;
    features: string[];
    summary: string[];
}

export const PLANS: Record<PlanId, PlanDef> = {
    ess: {
        id: 'ess', name: 'Essentials', base: 450, per: 3.60, monthly: false, liveHours: 0, voiceMinutes: 0, sms: 2, ai: 10, claude: 0, recHours: 0, bcastHours: 0, videos: 0,
        features: ['lms', 'cbt', 'admissions', 'games'],
        summary: ['Branded sub-address, name and logo', 'LMS, manual CBT, attendance, timetable, report cards', 'Parent communication and admissions', 'Light AI help for teachers'],
    },
    std: {
        id: 'std', name: 'Standard', base: 750, per: 7.20, monthly: false, liveHours: 1, voiceMinutes: 100, sms: 4, ai: 30, claude: 100, recHours: 20, bcastHours: 0, videos: 0,
        features: ['lms', 'cbt', 'admissions', 'games', 'finance', 'analytics', 'iqa', 'presentations', 'aicpd', 'ai_tutor', 'scholarships', 'cambridge', 'live_classes'],
        summary: ['Everything in Essentials', 'AI tutor, AI CBT, MEF-AID, presentations, AI CPD', 'Monthly Cambridge tests, Cambridge exams, IQA Hub', 'Finance, payroll and analytics', '1 live-class hour per student per month, 20 recorded hours a term'],
    },
    pre: {
        id: 'pre', name: 'Premium', base: 1200, per: 12.60, monthly: false, liveHours: 4, voiceMinutes: 300, sms: 6, ai: 60, claude: 300, recHours: 100, bcastHours: 10, videos: 0,
        features: ['lms', 'cbt', 'admissions', 'games', 'finance', 'analytics', 'iqa', 'presentations', 'aicpd', 'ai_tutor', 'scholarships', 'cambridge', 'live_classes', 'tahfiz', 'musabaqa'],
        summary: ['Everything in Standard', 'Tahfiz with the AI recitation coach, Musabaqa', '4 live-class hours per student per month, 100 recorded hours and 10 broadcast hours a term', 'Custom domain included, named support contact'],
    },
    trn: {
        id: 'trn', name: 'Training', base: 300, per: 3.30, monthly: true, liveHours: 6, voiceMinutes: 100, sms: 1, ai: 30, claude: 50, recHours: 20, bcastHours: 0, videos: 0,
        features: ['lms', 'cbt', 'finance', 'analytics', 'presentations', 'aicpd', 'ai_tutor', 'live_classes'],
        summary: ['For training centres, colleges and academies', 'Courses, CBT and certificates', '6 live-class hours per learner per month', 'Billed monthly per learner'],
    },
    ent: {
        id: 'ent', name: 'Enterprise', base: 0, per: 0, monthly: false, contact: true, liveHours: 0, voiceMinutes: 0, sms: 0, ai: 0, claude: 0, recHours: 0, bcastHours: 0, videos: 0,
        features: ['lms', 'cbt', 'admissions', 'games', 'finance', 'analytics', 'iqa', 'presentations', 'aicpd', 'ai_tutor', 'scholarships', 'cambridge', 'live_classes', 'tahfiz', 'musabaqa'],
        summary: ['School groups, large schools, ministries and organisations', 'Own-brand edition, heavy live teaching and broadcasting', 'Voice tutor and AI intro videos at agreed volumes', 'Prices and caps agreed with MEF; contact us'],
    },
};

/** Enterprise terms agreed with MEF, set by Super Admin on the school's billing record */
export interface CustomTerms {
    /** dollars per term (or month) */
    base: number; per: number; monthly?: boolean;
    /** caps per period, in the plan's own units (hours, minutes, per-student counts as for the plans) */
    liveHours?: number; voiceMinutes?: number; sms?: number; ai?: number; claude?: number; recHours?: number; bcastHours?: number; videos?: number;
}
/** a plan, or for Enterprise the plan with the agreed terms laid over it */
export function planWith(plan: PlanId, custom?: CustomTerms | null): PlanDef {
    const p = PLANS[plan];
    if (!p.contact || !custom) return p;
    const c = Object.fromEntries(Object.entries(custom).filter(([, v]) => v != null)) as Partial<CustomTerms>;
    return { ...p, ...c, monthly: !!custom.monthly } as PlanDef;
}

export type AddOnId = 'tahfiz' | 'live' | 'voice' | 'sms' | 'ai' | 'claude' | 'rec' | 'bcast' | 'storage' | 'onboarding' | 'domain';
export const ADD_ONS: Record<AddOnId, { name: string; unit: string; usd: number; step: number }> = {
    tahfiz: { name: 'Tahfiz pack', unit: 'per student per term', usd: 2.25, step: 50 },
    live: { name: 'Live-hour bundle', unit: '1,000 student-hours', usd: 330, step: 5000 },
    voice: { name: 'Voice tutor bundle', unit: '1,000 minutes', usd: 225, step: 5000 },
    sms: { name: 'SMS bundle', unit: '1,000 messages', usd: 45, step: 500 },
    ai: { name: 'AI bundle', unit: '10,000 AI requests', usd: 135, step: 5000 },
    claude: { name: 'Presentations bundle', unit: '100 presentations or long documents', usd: 24, step: 500 },
    rec: { name: 'Recording bundle', unit: '50 recorded class hours', usd: 30, step: 500 },
    bcast: { name: 'Broadcast bundle', unit: '10 broadcast hours', usd: 30, step: 500 },
    storage: { name: 'Recording storage', unit: '100 GB for a year', usd: 180, step: 5000 },
    onboarding: { name: 'Onboarding and training', unit: 'once', usd: 570, step: 5000 },
    domain: { name: 'Custom domain', unit: 'once (free on Premium)', usd: 165, step: 5000 },
};

export interface PricingSettings {
    /** the rate prices are built from (₦ per $1) */
    appliedRate: number;
    appliedAt?: number;
    /** the last official rate entered */
    latestRate?: number;
    latestAt?: number;
    /** percent off the subscription for paying for the whole year */
    yearlyPct: number;
    /** percent off for paying for next term before it starts */
    earlyPct: number;
    /** percent of a referred school's first paid term credited to the school that referred it */
    referralPct: number;
    /** rate changes smaller than this (percent) leave prices as they are */
    bandPct: number;
    vatPct: number;
    /** percent off for a two-year agreement */
    twoYearPct?: number;
    /** percent off for the first institutions to join */
    foundingPct?: number;
    /** percent off by students on the roll: [minimum students, percent], highest first */
    volume?: [number, number][];
    /** all discounts together never take more than this percent off the subscription */
    maxDiscountPct?: number;
    bank?: { bankName?: string; accountName?: string; accountNumber?: string };
}

export const DEFAULT_PRICING: PricingSettings = {
    appliedRate: 1330, yearlyPct: 10, earlyPct: 5, referralPct: 10, bandPct: 5, vatPct: 7.5,
    twoYearPct: 10, foundingPct: 15, volume: [[2000, 20], [1000, 15], [500, 10]], maxDiscountPct: 50,
};
/** the volume discount for a number of students */
export const volumePct = (n: number, s: PricingSettings) => ((s.volume || DEFAULT_PRICING.volume!).find(([min]) => n >= min) || [0, 0])[1];

export const roundUp = (n: number, step: number) => Math.ceil(n / step) * step;
export const naira = (usd: number, rate: number, step: number) => roundUp(usd * rate, step);
export const basePrice = (p: PlanId, rate: number, custom?: CustomTerms | null) => { const d = planWith(p, custom); return naira(d.base, rate, d.monthly ? 1000 : 5000); };
export const perPrice = (p: PlanId, rate: number, custom?: CustomTerms | null) => naira(planWith(p, custom).per, rate, 50);
export const addOnPrice = (a: AddOnId, rate: number) => naira(ADD_ONS[a].usd, rate, ADD_ONS[a].step);

/** whether a newly entered official rate should replace the rate prices are built from */
export function rateDecision(current: number, latest: number, bandPct: number) {
    const move = current > 0 ? (latest - current) / current * 100 : 100;
    return { move, apply: Math.abs(move) > bandPct };
}

// ── Terms: first Sep–Dec, second Jan–Apr, third May–Aug (holidays included) ───────────────────────────
export interface Term { key: string; label: string; start: number; end: number }
const TERM_NAMES = ['First term', 'Second term', 'Third term'];
const lagos = (t: number) => new Date(t + 60 * 60 * 1000);   // Nigeria is UTC+1
const at = (y: number, m: number) => Date.UTC(y, m, 1) - 60 * 60 * 1000;

export function termAt(t: number): Term {
    const d = lagos(t), y = d.getUTCFullYear(), m = d.getUTCMonth();
    const n = m >= 8 ? 0 : m <= 3 ? 1 : 2;
    const year = n === 0 ? y : y - 1;
    const startMonth = [8, 0, 4][n], startYear = n === 0 ? year : year + 1;
    const start = at(startYear, startMonth);
    const end = at(startMonth + 4 > 11 ? startYear + 1 : startYear, (startMonth + 4) % 12) - 1;
    return { key: `${year}-${String((year + 1) % 100).padStart(2, '0')}-T${n + 1}`, label: `${TERM_NAMES[n]} ${year}/${String((year + 1) % 100).padStart(2, '0')}`, start, end };
}
export const nextTerm = (t: number) => termAt(termAt(t).end + 1);
export const monthKey = (t: number) => lagos(t).toISOString().slice(0, 7);
export function monthAt(t: number): Term {
    const d = lagos(t), y = d.getUTCFullYear(), m = d.getUTCMonth();
    const start = at(y, m), end = at(m === 11 ? y + 1 : y, (m + 1) % 12) - 1;
    return { key: monthKey(t), label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }), start, end };
}
/** the billing period a usage counter belongs to: the term for schools, the month for training */
export const usagePeriod = (plan: PlanId, t: number) => (PLANS[plan].monthly ? monthAt(t) : termAt(t));

// ── Quotes ─────────────────────────────────────────────────────────────────────────────────────────────
export interface QuoteInput {
    plan: PlanId;
    students: number;
    period: Period;
    /** pay for the year: 3 terms (12 months for training) */
    yearly: boolean;
    tahfiz?: number;
    live?: number;
    voice?: number;
    sms?: number;
    storage?: number;
    onboarding?: boolean;
    domain?: boolean;
    /** parents pay the fee per student through the portal (e-learning levy) */
    parentLevy?: boolean;
    /** naira credit from referrals, taken off the total */
    credit?: number;
    /** discounts Super Admin has granted this school (percent where a number) */
    twoYear?: boolean; founding?: boolean; community?: number;
    ai?: number; claude?: number; rec?: number; bcast?: number;
    /** Enterprise: the agreed terms */
    custom?: CustomTerms | null;
}
export interface QuoteLine { label: string; amount: number; kind: 'subscription' | 'addon' | 'once' | 'discount' }
export interface Quote {
    lines: QuoteLine[]; subtotal: number; vat: number; total: number; rate: number;
    plan: PlanId; students: number; periods: number; coverStart: number; coverEnd: number; coverLabel: string;
    prorate: number; usd: number;
}

export function quote(q: QuoteInput, s: PricingSettings, now = Date.now()): Quote {
    const plan = planWith(q.plan, q.custom), rate = s.appliedRate;
    if (plan.contact && !q.custom) throw new Error('Enterprise is priced by agreement. Contact MEF at info@muyikaratu.com.');
    const first = plan.monthly ? (q.period === 'next' ? monthAt(monthAt(now).end + 1) : monthAt(now)) : (q.period === 'next' ? nextTerm(now) : termAt(now));
    const periods = q.yearly ? (plan.monthly ? 12 : 3) : 1;
    let last = first;
    for (let i = 1; i < periods; i++) last = plan.monthly ? monthAt(last.end + 1) : termAt(last.end + 1);
    // joining part-way through the current term: pay for the months left (whole months, at least one)
    const prorate = plan.monthly || q.period === 'next' ? 1 : Math.min(1, Math.max(1, Math.ceil((first.end - now) / (30.44 * 864e5))) / 4);
    const unit = plan.monthly ? 'month' : 'term';
    const n = Math.max(1, Math.floor(q.students));
    const lines: QuoteLine[] = [];
    const sub = (label: string, amount: number) => lines.push({ label, amount: Math.round(amount), kind: 'subscription' });
    const termsOf = periods - 1 + prorate;   // e.g. 0.75 of this term, or 3 terms
    const base = basePrice(q.plan, rate, q.custom), per = perPrice(q.plan, rate, q.custom);
    sub(`${plan.name} base subscription${termsOf !== 1 ? ` × ${+termsOf.toFixed(2)} ${unit}s` : ''}`, base * termsOf);
    if (!q.parentLevy) sub(`${n.toLocaleString('en-NG')} ${plan.monthly ? 'learners' : 'students'} × ₦${per.toLocaleString('en-NG')}${termsOf !== 1 ? ` × ${+termsOf.toFixed(2)}` : ''}`, n * per * termsOf);
    const tahfiz = q.plan === 'pre' || q.plan === 'trn' || q.plan === 'ent' ? 0 : Math.min(n, Math.max(0, Math.floor(q.tahfiz || 0)));
    if (tahfiz) sub(`Tahfiz pack: ${tahfiz} students × ₦${addOnPrice('tahfiz', rate).toLocaleString('en-NG')}`, tahfiz * addOnPrice('tahfiz', rate) * termsOf);
    const subscriptionTotal = lines.reduce((a, l) => a + l.amount, 0);
    for (const [k, count] of [['live', q.live], ['voice', q.voice], ['sms', q.sms], ['ai', q.ai], ['claude', q.claude], ['rec', q.rec], ['bcast', q.bcast], ['storage', q.storage]] as [AddOnId, number | undefined][]) {
        const c = Math.max(0, Math.floor(count || 0));
        if (c) lines.push({ label: `${ADD_ONS[k].name} (${ADD_ONS[k].unit}) × ${c}`, amount: c * addOnPrice(k, rate), kind: 'addon' });
    }
    if (q.onboarding) lines.push({ label: ADD_ONS.onboarding.name, amount: addOnPrice('onboarding', rate), kind: 'once' });
    if (q.domain && q.plan !== 'pre' && q.plan !== 'ent') lines.push({ label: ADD_ONS.domain.name, amount: addOnPrice('domain', rate), kind: 'once' });
    // discounts add together, up to the ceiling, and apply to the subscription only (not bundles or one-offs)
    const parts: [string, number][] = [];
    if (q.yearly && s.yearlyPct > 0) parts.push(['whole year', s.yearlyPct]);
    if (q.period === 'next' && now < first.start && s.earlyPct > 0) parts.push(['early payment', s.earlyPct]);
    if (q.twoYear && (s.twoYearPct ?? 0) > 0) parts.push(['two-year agreement', s.twoYearPct!]);
    if (!plan.contact && !plan.monthly) { const v = volumePct(n, s); if (v > 0) parts.push([`${n.toLocaleString('en-NG')} students`, v]); }
    if (q.founding && (s.foundingPct ?? 0) > 0) parts.push(['founding partner', s.foundingPct!]);
    if (q.community) parts.push(['community school', Math.min(20, Math.max(0, q.community))]);
    const cap = s.maxDiscountPct ?? 50, sum = parts.reduce((a, [, p]) => a + p, 0), pct = Math.min(cap, sum);
    if (pct > 0) lines.push({ label: `Discount: ${parts.map(([k, p]) => `${k} ${p}%`).join(' + ')}${sum > cap ? ` (capped at ${cap}%)` : ''} = ${pct}% off the subscription`, amount: -Math.round(subscriptionTotal * pct / 100), kind: 'discount' });
    let subtotal = lines.reduce((a, l) => a + l.amount, 0);
    const credit = Math.min(Math.max(0, Math.floor(q.credit || 0)), subtotal);
    if (credit) { lines.push({ label: 'Referral credit', amount: -credit, kind: 'discount' }); subtotal -= credit; }
    const vat = Math.round(subtotal * s.vatPct / 100);
    return {
        lines, subtotal, vat, total: subtotal + vat, rate, plan: q.plan, students: n, periods,
        coverStart: q.period === 'current' ? now : first.start, coverEnd: last.end,
        coverLabel: periods > 1 ? `${first.label} to ${last.label}` : first.label, prorate, usd: Math.round((subtotal + vat) / rate * 100) / 100,
    };
}

/** what a plan includes for one billing period (a term, or a month for training), for n students */
export function allowance(plan: PlanId, n: number, custom?: CustomTerms | null): Caps {
    const p = planWith(plan, custom), months = p.monthly ? 1 : 4;
    return {
        live: Math.round(n * p.liveHours * 60 * months), voice: p.voiceMinutes * months, sms: n * p.sms, ai: n * p.ai * months,
        claude: p.claude, rec: p.recHours * 60, bcast: p.bcastHours * 60, video: p.videos,
    };
}
/** what one bundle adds (minutes for live, recording and broadcast) */
export const BUNDLE: Partial<Caps> = { live: 1000 * 60, voice: 1000, sms: 1000, ai: 10000, claude: 100, rec: 50 * 60, bcast: 10 * 60 };

/** the e-learning levy one parent pays per student per term (per month for training) */
export const levyPrice = (plan: PlanId, rate: number, custom?: CustomTerms | null) => perPrice(plan, rate, custom);
